// Typed wrappers around the backend. In production the base URL is the
// Cloud Run service (VITE_API_URL, baked at build time); in dev the Vite
// proxy forwards /api to the local backend so this stays relative.

import { getAccessToken } from "../auth/session";
import type {
  Allocation,
  AssignmentEvent,
  Carrier,
  CarrierDocument,
  CarrierInvoice,
  ChargeKind,
  CostAssignment,
  DeliveriesResponse,
  DeliveryFlag,
  DeliveryOutcome,
  DeliverySearch,
  DeliveryUpdate,
  DispatchFilter,
  DispatchPlan,
  Disposition,
  DocumentKind,
  ExceptionCode,
  FollowUps,
  DraftDetail,
  DraftSummary,
  JobStatus,
  OptimizeResponseDTO,
  OrderHistory,
  OrderPalletsResponse,
  OrderSummary,
  ManualStage,
  PlanCarriers,
  PlanDocument,
  PlanOrderStatus,
  PlanRoute,
  PlanSource,
  PlanSummary,
  PlanTruck,
  PreflightIssue,
  ProofPhoto,
  PublicOrder,
  RefreshedDeliveries,
  RunDate,
  RunDetail,
  RunSummary,
  StatusGroup,
} from "../types";

const API_BASE = (import.meta.env.VITE_API_URL ?? "") + "/api";

export interface FleetOptions {
  num_26ft: number;
  num_53ft: number;
  // When each truck leaves (26 ft trucks first), minutes from midnight.
  departures: number[];
  // 3PL trucks the optimizer may call, not must: it uses one only when it
  // pays. Sent to the engine as 2 trip slots each (a 3PL truck can come
  // back to reload once), as the engine pairs them.
  num_3pl: number;
  // When each of those 3PL trucks comes to load, minutes from midnight.
  threepl_departures: number[];
}

const THREEPL_TRIPS_PER_TRUCK = 2;

// A fleet's departures as repeated `departures` (and `threepl_departures`)
// query parameters.
function departureParams(
  params: URLSearchParams,
  fleet: { departures: number[]; threepl_departures?: number[] },
) {
  for (const m of fleet.departures) params.append("departures", String(m));
  for (const m of fleet.threepl_departures ?? []) params.append("threepl_departures", String(m));
  return params;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function submitOptimize(
  file: File,
  fleet: FleetOptions,
): Promise<{ job_id: string }> {
  const params = departureParams(
    new URLSearchParams({
      num_26ft: String(fleet.num_26ft),
      num_53ft: String(fleet.num_53ft),
      num_3pl_slots: String(fleet.num_3pl * THREEPL_TRIPS_PER_TRUCK),
    }),
    fleet,
  );
  const form = new FormData();
  form.append("file", file);
  const resp = await fetch(`${API_BASE}/optimize?${params}`, {
    method: "POST",
    headers: await authHeaders(),
    body: form,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new Error(`Optimize failed (${resp.status}): ${text}`);
  }
  return resp.json();
}

// Evaluate a client-fixed plan from a CSV — a `trip_id` column groups orders
// into trips. Runs as-is and reports per-trip deviations. Needs no DB.
export async function submitEvaluateCsv(
  file: File,
  fleet: FleetOptions,
): Promise<{ job_id: string }> {
  const params = departureParams(
    new URLSearchParams({
      num_26ft: String(fleet.num_26ft),
      num_53ft: String(fleet.num_53ft),
    }),
    fleet,
  );
  const form = new FormData();
  form.append("file", file);
  const resp = await fetch(`${API_BASE}/optimize/evaluate-csv?${params}`, {
    method: "POST",
    headers: await authHeaders(),
    body: form,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new Error(`Evaluate failed (${resp.status}): ${text}`);
  }
  return resp.json();
}

// ── Order picker ──────────────────────────────────────────────────────────

export async function listOrders(
  params: {
    days?: number;
    zip?: string;
    search?: string;
    region?: string;
    dispatch?: DispatchFilter;
  } = {},
): Promise<OrderSummary[]> {
  const q = new URLSearchParams();
  if (params.days != null) q.set("days", String(params.days));
  if (params.zip) q.set("zip", params.zip);
  if (params.search) q.set("search", params.search);
  // Omitted means every region — what every caller did before regions existed.
  if (params.region) q.set("region", params.region);
  if (params.dispatch) q.set("dispatch", params.dispatch);
  const resp = await fetch(`${API_BASE}/orders?${q}`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new Error(`Couldn't load orders (${resp.status}): ${text}`);
  }
  return resp.json();
}

export async function getOrderPallets(
  orderNo: string,
): Promise<OrderPalletsResponse> {
  const resp = await fetch(
    `${API_BASE}/orders/${encodeURIComponent(orderNo)}/pallets`,
    { headers: await authHeaders() },
  );
  if (!resp.ok) throw new Error(`GET /orders/${orderNo}/pallets → ${resp.status}`);
  return resp.json();
}

export interface ResolveOrdersResponse {
  found: OrderSummary[];
  missing: string[];
}

// Resolve a pasted list of order numbers to picker rows (case/whitespace
// insensitive). `missing` lists tokens with no DB match so typos surface.
export async function resolveOrders(
  orderNos: string[],
): Promise<ResolveOrdersResponse> {
  const resp = await fetch(`${API_BASE}/orders/resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ order_nos: orderNos }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new Error(`Couldn't resolve orders (${resp.status}): ${text}`);
  }
  return resp.json();
}

export interface PalletOverridePayload {
  length_ft: number;
  width_ft: number;
  height_ft: number;
  weight_kg: number;
}

export interface OrderSelectionPayload {
  order_no: string;
  deadline_clock?: string;
  arrival_after_clock?: string;
  delivery_group?: string;
  pallets?: PalletOverridePayload[];
  dest_zip?: string; // required for manual orders (not in the synced store)
  rev_per_kg?: number;
  trip_id?: string; // client's trip assignment for the evaluate path
  truck_id?: string; // physical truck; trips sharing it run sequentially
}

export async function submitOptimizeFromOrders(
  orders: OrderSelectionPayload[],
  fleet: FleetOptions,
): Promise<{ job_id: string }> {
  const resp = await fetch(`${API_BASE}/optimize/from-orders`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({
      orders,
      num_26ft: fleet.num_26ft,
      num_53ft: fleet.num_53ft,
      departures: fleet.departures,
      num_3pl_slots: fleet.num_3pl * THREEPL_TRIPS_PER_TRUCK,
      threepl_departures: fleet.threepl_departures,
    }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new Error(`Optimize failed (${resp.status}): ${text}`);
  }
  return resp.json();
}

// Evaluate a client-fixed order->trip plan (orders carry trip_id). The backend
// runs it without failing and reports per-trip constraint deviations.
export async function submitEvaluatePlan(
  orders: OrderSelectionPayload[],
  fleet: FleetOptions,
): Promise<{ job_id: string }> {
  const resp = await fetch(`${API_BASE}/optimize/evaluate-plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({
      orders,
      num_26ft: fleet.num_26ft,
      num_53ft: fleet.num_53ft,
      departures: fleet.departures,
    }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new Error(`Evaluate failed (${resp.status}): ${text}`);
  }
  return resp.json();
}

// ── Run history ─────────────────────────────────────────────────────────

export async function listRunDates(): Promise<RunDate[]> {
  const resp = await fetch(`${API_BASE}/runs/dates`, { headers: await authHeaders() });
  if (!resp.ok) throw new Error(`GET /runs/dates → ${resp.status}`);
  return resp.json();
}

export async function listRuns(date: string): Promise<RunSummary[]> {
  const resp = await fetch(`${API_BASE}/runs?date=${encodeURIComponent(date)}`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`GET /runs → ${resp.status}`);
  return resp.json();
}

export async function getRun(id: string): Promise<RunDetail> {
  const resp = await fetch(`${API_BASE}/runs/${encodeURIComponent(id)}`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`GET /runs/${id} → ${resp.status}`);
  return resp.json();
}

// Download the exact input a run was built from (original CSV for upload
// runs, the request payload as JSON for picker runs). Returns the raw bytes
// plus the server-suggested filename for the browser save dialog.
export async function downloadRunInput(
  id: string,
): Promise<{ blob: Blob; filename: string }> {
  const resp = await fetch(`${API_BASE}/runs/${encodeURIComponent(id)}/input`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`GET /runs/${id}/input → ${resp.status}`);
  const cd = resp.headers.get("Content-Disposition") || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  return { blob: await resp.blob(), filename: m?.[1] ?? `run-${id}` };
}

// ── Composition drafts (save/resume the order-builder cart) ────────────────

export async function listDrafts(): Promise<DraftSummary[]> {
  const resp = await fetch(`${API_BASE}/drafts`, { headers: await authHeaders() });
  if (!resp.ok) throw new Error(`GET /drafts → ${resp.status}`);
  return resp.json();
}

export async function getDraft(id: string): Promise<DraftDetail> {
  const resp = await fetch(`${API_BASE}/drafts/${encodeURIComponent(id)}`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`GET /drafts/${id} → ${resp.status}`);
  return resp.json();
}

export async function saveDraft(
  label: string,
  payload: Record<string, unknown>,
): Promise<DraftSummary> {
  const resp = await fetch(`${API_BASE}/drafts`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ label, payload }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new Error(`Couldn't save draft (${resp.status}): ${text}`);
  }
  return resp.json();
}

export async function deleteDraft(id: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/drafts/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`DELETE /drafts/${id} → ${resp.status}`);
}

// ── Dispatch plans ──────────────────────────────────────────────────────────

// Plan endpoints answer 409 (stale version, can't publish yet) and 422 (an
// edit the server rejects) with a structured `detail`. ApiError keeps the
// status and that body so the page can react to them.
export class ApiError extends Error {
  status: number;
  detail: unknown;

  constructor(status: number, detail: unknown) {
    const message =
      typeof detail === "string"
        ? detail
        : ((detail as { message?: string } | null)?.message ?? `Request failed (${status})`);
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function planRequest(path: string, body?: unknown): Promise<DispatchPlan> {
  const headers: Record<string, string> = await authHeaders();
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const resp = await fetch(`${API_BASE}/dispatch-plans${path}`, {
    method: body === undefined ? "GET" : "POST",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!resp.ok) {
    const parsed = await resp.json().catch(() => null);
    throw new ApiError(resp.status, parsed?.detail ?? resp.statusText);
  }
  return resp.json();
}

// Open the plan for a saved run, creating (and validating) it the first time.
export function createPlan(source: PlanSource): Promise<DispatchPlan> {
  return planRequest("", source);
}

export function getPlan(id: string): Promise<DispatchPlan> {
  return planRequest(`/${encodeURIComponent(id)}`);
}

// Build a draft plan by hand from the cart's orders: each on its trip of
// `document` (Evaluate mode's trucks and trips) or, with no route, unrouted.
export function createPlanByHand(
  orders: AddedOrderPayload[],
  planDate: string,
  document?: { trucks: PlanTruck[]; routes: PlanRoute[] },
): Promise<DispatchPlan> {
  return planRequest("", { orders, plan_date: planDate, document });
}

// Each routed order's status on the plan, by the rule every tab uses.
export async function getPlanStatuses(id: string): Promise<PlanOrderStatus[]> {
  const resp = await fetch(`${API_BASE}/dispatch-plans/${encodeURIComponent(id)}/statuses`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) {
    const parsed = await resp.json().catch(() => null);
    throw new ApiError(resp.status, parsed?.detail ?? resp.statusText);
  }
  return (await resp.json()).statuses;
}

// The dispatcher's saved plans, newest plan date first, within the plan dates given.
export async function listPlans(
  params: { dateFrom?: string; dateTo?: string } = {},
): Promise<PlanSummary[]> {
  const q = new URLSearchParams();
  if (params.dateFrom) q.set("date_from", params.dateFrom);
  if (params.dateTo) q.set("date_to", params.dateTo);
  const resp = await fetch(`${API_BASE}/dispatch-plans?${q}`, { headers: await authHeaders() });
  if (!resp.ok) {
    const parsed = await resp.json().catch(() => null);
    throw new ApiError(resp.status, parsed?.detail ?? resp.statusText);
  }
  return (await resp.json()).plans;
}

// The plan as saved, as the dispatcher's Excel workbook, with its headers in `lang`.
export async function downloadPlanWorkbook(
  id: string,
  lang: string,
): Promise<{ blob: Blob; filename: string }> {
  const q = new URLSearchParams({ lang });
  const resp = await fetch(`${API_BASE}/dispatch-plans/${encodeURIComponent(id)}/export.xlsx?${q}`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) {
    const parsed = await resp.json().catch(() => null);
    throw new ApiError(resp.status, parsed?.detail ?? resp.statusText);
  }
  const m = /filename="?([^"]+)"?/.exec(resp.headers.get("Content-Disposition") || "");
  return { blob: await resp.blob(), filename: m?.[1] ?? `dispatch-plan-${id}.xlsx` };
}

// An order the dispatcher adds to a plan, and the route it goes on. A synced
// order needs only its number (`pallets` replaces the derived ones); a
// manual order brings its ZIP and pallets.
export interface AddedOrderPayload {
  order_no: string;
  route_id?: string; // none: into the unrouted orders
  deadline_clock?: string; // "HH:MM"
  arrival_after_clock?: string;
  pallets?: PalletOverridePayload[]; // feet and kg
  manual?: boolean;
  dest_zip?: string;
  customer_name?: string;
  address?: string;
  delivery_notes?: string;
}

// Save the edited plan (made on `version`), with any orders added, and
// revalidate it on the server.
export function validatePlan(
  id: string,
  version: number,
  planDate: string,
  document: PlanDocument,
  addedOrders: AddedOrderPayload[] = [],
): Promise<DispatchPlan> {
  return planRequest(`/${encodeURIComponent(id)}/validate`, {
    version,
    plan_date: planDate,
    document,
    added_orders: addedOrders,
  });
}

// Put the optimizer's plan back (discards every edit) as a new version.
export function resetPlan(id: string, version: number): Promise<DispatchPlan> {
  return planRequest(`/${encodeURIComponent(id)}/reset`, { version });
}

// Accept a partially published plan as it stands: its unsent routes stay unsent.
export function closePlan(id: string, version: number): Promise<DispatchPlan> {
  return planRequest(`/${encodeURIComponent(id)}/close`, { version });
}

// Fetch how the sent routes are going: arrivals, departures, proof of delivery.
export function refreshDelivery(id: string): Promise<DispatchPlan> {
  return planRequest(`/${encodeURIComponent(id)}/delivery/refresh`, {});
}

// Publish the routes not yet sent (all of them the first time).
export function publishPlan(
  id: string,
  version: number,
  acknowledgeWarnings: boolean,
): Promise<DispatchPlan> {
  return planRequest(`/${encodeURIComponent(id)}/publish`, {
    version,
    acknowledge_warnings: acknowledgeWarnings,
  });
}

// Clear the plan's routes from drivers' apps; routes a driver started stay.
export function withdrawPlan(id: string, version: number): Promise<DispatchPlan> {
  return planRequest(`/${encodeURIComponent(id)}/withdraw`, { version });
}

// ── 3PL carriers and bookings ───────────────────────────────────────────────

async function jsonRequest<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!resp.ok) {
    const parsed = await resp.json().catch(() => null);
    throw new ApiError(resp.status, parsed?.detail ?? resp.statusText);
  }
  return resp.json();
}

// The shared carrier directory, by name.
export async function listCarriers(includeInactive = false): Promise<Carrier[]> {
  const q = includeInactive ? "?include_inactive=true" : "";
  return (await jsonRequest<{ carriers: Carrier[] }>(`/carriers${q}`)).carriers;
}

// Add a carrier; 409 (with the clashing `carrier_id`) when the name is taken.
export function addCarrier(fields: {
  name: string;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
}): Promise<Carrier> {
  return jsonRequest("/carriers", "POST", fields);
}

// Change a carrier's details, or deactivate it.
export function editCarrier(id: string, changes: Partial<Carrier>): Promise<Carrier> {
  return jsonRequest(`/carriers/${encodeURIComponent(id)}`, "PATCH", changes);
}

// A plan's 3PL trucks, their loads and bookings.
export function getPlanCarriers(planId: string): Promise<PlanCarriers> {
  return jsonRequest(`/dispatch-plans/${encodeURIComponent(planId)}/carrier-assignments`);
}

// Assign a carrier to a 3PL truck of the plan version seen, as requested or booked.
export function assignCarrier(
  planId: string,
  body: { truck_id: string; carrier_id: string; status: "requested" | "booked"; plan_version: number },
): Promise<PlanCarriers> {
  return jsonRequest(
    `/dispatch-plans/${encodeURIComponent(planId)}/carrier-assignments`,
    "POST",
    body,
  );
}

function assignmentPath(id: string, action: string): string {
  return `/carrier-assignments/${encodeURIComponent(id)}/${action}`;
}

// The requested carrier accepted the truck's load.
export function bookAssignment(id: string, version: number, planVersion: number) {
  return jsonRequest<PlanCarriers>(assignmentPath(id, "book"), "POST", {
    version,
    plan_version: planVersion,
  });
}

// Cancel a booking or request, with why.
export function cancelAssignment(id: string, version: number, reason: string) {
  return jsonRequest<PlanCarriers>(assignmentPath(id, "cancel"), "POST", { version, reason });
}

// The carrier was told about the plan's changes to its truck.
export function confirmCarrierInformed(id: string, version: number, planVersion: number) {
  return jsonRequest<PlanCarriers>(assignmentPath(id, "carrier-informed"), "POST", {
    version,
    plan_version: planVersion,
  });
}

// Record a negotiated charge line.
export function addCharge(
  id: string,
  body: { version: number; kind: ChargeKind; amount_usd: number; code?: string; note?: string },
) {
  return jsonRequest<PlanCarriers>(assignmentPath(id, "charges"), "POST", body);
}

// Void a charge line, with why.
export function voidCharge(id: string, chargeId: string, version: number, reason: string) {
  return jsonRequest<PlanCarriers>(
    assignmentPath(id, `charges/${encodeURIComponent(chargeId)}/void`),
    "POST",
    { version, reason },
  );
}

// ── 3PL costs ───────────────────────────────────────────────────────────────

// Every booked truckload in the plan dates with its costs, every dispatcher's.
export async function listCarrierCosts(params: {
  dateFrom: string;
  dateTo: string;
  carrierId?: string;
}): Promise<CostAssignment[]> {
  const q = new URLSearchParams({ date_from: params.dateFrom, date_to: params.dateTo });
  if (params.carrierId) q.set("carrier_id", params.carrierId);
  return (await jsonRequest<{ assignments: CostAssignment[] }>(`/carrier-costs?${q}`)).assignments;
}

// The costs as CSV, a row per charge line.
export async function downloadCarrierCosts(
  dateFrom: string,
  dateTo: string,
): Promise<{ blob: Blob; filename: string }> {
  const q = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  const resp = await deliveryRequest(`/carrier-costs/export?${q}`);
  const m = /filename="?([^"]+)"?/.exec(resp.headers.get("Content-Disposition") || "");
  return { blob: await resp.blob(), filename: m?.[1] ?? `3pl-costs-${dateFrom}-${dateTo}.csv` };
}

// Type in a carrier's invoice, each line on the truckload (at the version seen) it bills.
export function recordCarrierInvoice(body: {
  carrier_id: string;
  invoice_no: string;
  invoice_date?: string;
  service_date: string;
  total_usd: number;
  note?: string;
  lines: {
    assignment_id: string;
    version: number;
    kind: ChargeKind;
    amount_usd: number;
    code?: string;
  }[];
}): Promise<CarrierInvoice> {
  return jsonRequest("/carrier-invoices", "POST", body);
}

// Void an invoice and its lines, with why.
export function voidCarrierInvoice(id: string, reason: string): Promise<CarrierInvoice> {
  return jsonRequest(`/carrier-invoices/${encodeURIComponent(id)}/void`, "POST", { reason });
}

// Freeze a truckload's invoiced total as its reconciled cost.
export async function reconcileCost(id: string, version: number): Promise<void> {
  await deliveryRequest(assignmentPath(id, "reconcile"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ version }),
  });
}

// Unfreeze a reconciled truckload's cost, with why.
export async function reopenCost(id: string, version: number, reason: string): Promise<void> {
  await deliveryRequest(assignmentPath(id, "reopen"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ version, reason }),
  });
}

// A truckload's audit trail, oldest first.
export function getAssignmentHistory(id: string): Promise<AssignmentEvent[]> {
  return jsonRequest(assignmentPath(id, "history"));
}

// A truckload's cost split over its orders: frozen at reconcile, else as of now.
export function getAllocation(id: string): Promise<Allocation> {
  return jsonRequest(assignmentPath(id, "allocation"));
}

export async function downloadOrderCosts(
  dateFrom: string,
  dateTo: string,
): Promise<{ blob: Blob; filename: string }> {
  const q = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  const resp = await deliveryRequest(`/carrier-costs/export-orders?${q}`);
  const m = /filename="?([^"]+)"?/.exec(resp.headers.get("Content-Disposition") || "");
  return {
    blob: await resp.blob(),
    filename: m?.[1] ?? `3pl-order-costs-${dateFrom}-${dateTo}.csv`,
  };
}

// Keep a paper (a photo or a PDF) for a truckload at the version seen.
export async function uploadCarrierDocument(
  id: string,
  version: number,
  file: File,
  kind: DocumentKind,
): Promise<CarrierDocument> {
  const form = new FormData();
  form.append("file", file);
  form.append("kind", kind);
  form.append("version", String(version));
  return (await deliveryRequest(assignmentPath(id, "documents"), { method: "POST", body: form })).json();
}

// Remove a truckload's paper from view.
export async function removeCarrierDocument(
  id: string,
  documentId: string,
  version: number,
): Promise<void> {
  const path = assignmentPath(id, `documents/${encodeURIComponent(documentId)}?version=${version}`);
  await deliveryRequest(path, { method: "DELETE" });
}

// A truckload's paper, as uploaded; it needs the login, so it is fetched.
export async function fetchCarrierDocument(documentId: string): Promise<Blob> {
  return (await deliveryRequest(`/carrier-documents/${encodeURIComponent(documentId)}`)).blob();
}

// ── Delivery records ────────────────────────────────────────────────────────

async function deliveryRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const resp = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...(await authHeaders()), ...(init.headers ?? {}) },
  });
  if (!resp.ok) {
    const parsed = await resp.json().catch(() => null);
    throw new ApiError(resp.status, parsed?.detail ?? resp.statusText);
  }
  return resp;
}

// A day's delivery records at a depot; without a date, today when it has sent
// routes, else the latest day that has any.
export async function listDeliveries(params: {
  depotZip: string;
  planDate?: string;
}): Promise<DeliveriesResponse> {
  const q = new URLSearchParams({ depot_zip: params.depotZip });
  if (params.planDate) q.set("plan_date", params.planDate);
  return (await deliveryRequest(`/deliveries?${q}`)).json();
}

// The dispatcher's orders found by number, pallet id or uShipment order id,
// across every date and depot, narrowed to a status group when given.
export async function searchDeliveries(q: string, group?: StatusGroup): Promise<DeliverySearch> {
  const params = new URLSearchParams({ q });
  if (group) params.set("group", group);
  return (await deliveryRequest(`/deliveries/search?${params}`)).json();
}

// Read a day at a depot from Samsara now, and get it back as it then stands.
export async function refreshDeliveries(
  planDate: string,
  depotZip: string,
): Promise<RefreshedDeliveries> {
  const q = new URLSearchParams({ plan_date: planDate, depot_zip: depotZip });
  return (await deliveryRequest(`/deliveries/refresh?${q}`, { method: "POST" })).json();
}

// A stamped photo of one of the dispatcher's deliveries (problem photos too).
export async function fetchDeliveryPhoto(mediaId: string): Promise<Blob> {
  return (await deliveryRequest(`/deliveries/photos/${encodeURIComponent(mediaId)}`)).blob();
}

// Upload a drop's proof of delivery (a photo or a PDF) when its driver gave none.
// Upload one proof to one or more of a plan's drops; the file is stored once.
export async function uploadDeliveryProof(
  planId: string,
  dropKeys: string[],
  file: File,
  kind: string,
): Promise<ProofPhoto[]> {
  const form = new FormData();
  form.append("file", file);
  form.append("kind", kind);
  for (const key of dropKeys) form.append("drop_keys", key);
  const path = `/deliveries/${encodeURIComponent(planId)}/photos`;
  return (await deliveryRequest(path, { method: "POST", body: form })).json();
}

// Remove one of the dispatcher's own uploads.
export async function deleteDeliveryProof(mediaId: string): Promise<void> {
  await deliveryRequest(`/deliveries/photos/${encodeURIComponent(mediaId)}`, { method: "DELETE" });
}

// Mark an order delivered on the dispatcher's word (`delivered`), or undo it.
export async function setMarkedDelivered(
  record: { plan_id: string; drop_key: string; order_no: string },
  delivered: boolean,
): Promise<void> {
  const path =
    `/deliveries/${encodeURIComponent(record.plan_id)}/drops/${encodeURIComponent(record.drop_key)}` +
    `/orders/${encodeURIComponent(record.order_no)}/delivered`;
  await deliveryRequest(path, { method: delivered ? "POST" : "DELETE" });
}

// Mark the reasons for attention the dispatcher saw on an order reviewed, with
// what was done about them; one that appeared since stays.
export async function reviewDelivery(
  record: { plan_id: string; drop_key: string; order_no: string },
  note: string,
  flags: DeliveryFlag[],
): Promise<void> {
  const path =
    `/deliveries/${encodeURIComponent(record.plan_id)}/drops/${encodeURIComponent(record.drop_key)}` +
    `/orders/${encodeURIComponent(record.order_no)}/review`;
  await deliveryRequest(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note, flags }),
  });
}

function orderPath(record: { plan_id: string; drop_key: string; order_no: string }, action: string) {
  return (
    `/deliveries/${encodeURIComponent(record.plan_id)}/drops/${encodeURIComponent(record.drop_key)}` +
    `/orders/${encodeURIComponent(record.order_no)}/${action}`
  );
}

async function postJson(path: string, body: unknown, method = "POST"): Promise<void> {
  await deliveryRequest(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

type OrderRef = { plan_id: string; drop_key: string; order_no: string; revision: number };

// The exception catalogue, retired codes included.
export async function getExceptionCodes(): Promise<ExceptionCode[]> {
  return (await deliveryRequest("/deliveries/exception-codes")).json();
}

// Record an order's outcome: over Samsara's, with why; or as its 3PL carrier
// reported it, `reason` being the note. 409 when the record moved on.
export async function recordOutcome(
  record: OrderRef,
  outcome: {
    stage: DeliveryOutcome;
    exception_code?: string;
    reason?: string;
    disposition?: Disposition;
    at?: string;
  },
): Promise<void> {
  await postJson(orderPath(record, "outcome"), { ...outcome, revision: record.revision });
}

// Use Samsara's: the dispatcher's outcome goes, with why.
export async function revertOutcome(record: OrderRef, reason: string): Promise<void> {
  await postJson(orderPath(record, "outcome"), { reason, revision: record.revision }, "DELETE");
}

// Another exception code for a failed or problem attempt; its outcome stands.
export async function recodeDelivery(record: OrderRef, code: string, note?: string): Promise<void> {
  await postJson(orderPath(record, "exception-code"), {
    exception_code: code,
    note,
    revision: record.revision,
  });
}

// What happens to a failed attempt's freight now.
export async function setDisposition(
  record: OrderRef,
  disposition: Disposition,
  note?: string,
): Promise<void> {
  await postJson(orderPath(record, "disposition"), {
    disposition,
    note,
    revision: record.revision,
  });
}

// Every attempt of an order, oldest first, each with its events.
export async function getOrderHistory(orderNo: string): Promise<OrderHistory> {
  return (await deliveryRequest(`/deliveries/orders/${encodeURIComponent(orderNo)}/history`)).json();
}

// A depot's open follow-ups, any day, narrowed to one disposition when given.
export async function listFollowUps(depotZip: string, disposition?: Disposition): Promise<FollowUps> {
  const q = new URLSearchParams({ depot_zip: depotZip });
  if (disposition) q.set("disposition", disposition);
  return (await deliveryRequest(`/deliveries/follow-ups?${q}`)).json();
}

// Record what a 3PL carrier reported for one order (or correct an earlier entry).
export async function recordOrderUpdate(
  record: { plan_id: string; drop_key: string; order_no: string },
  update: { stage: ManualStage; at?: string; note?: string },
): Promise<void> {
  const path =
    `/deliveries/${encodeURIComponent(record.plan_id)}/drops/${encodeURIComponent(record.drop_key)}` +
    `/orders/${encodeURIComponent(record.order_no)}/update`;
  await deliveryRequest(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(update),
  });
}

// Record what a 3PL carrier reported for a whole trip.
export async function recordTripUpdate(
  planId: string,
  routeId: string,
  update: { stage: ManualStage; at?: string; note?: string },
): Promise<void> {
  const path = `/deliveries/${encodeURIComponent(planId)}/routes/${encodeURIComponent(routeId)}/update`;
  await deliveryRequest(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(update),
  });
}

// Everything the dispatcher recorded for a 3PL order, oldest first.
export async function getDropUpdates(planId: string, dropKey: string): Promise<DeliveryUpdate[]> {
  const path = `/deliveries/${encodeURIComponent(planId)}/drops/${encodeURIComponent(dropKey)}/updates`;
  return (await deliveryRequest(path)).json();
}

// Replace an order's share link; the old one stops working at once.
export async function renewShareLink(
  orderNo: string,
): Promise<{ order_no: string; share_url: string }> {
  const path = `/deliveries/links/${encodeURIComponent(orderNo)}`;
  return (await deliveryRequest(path, { method: "POST" })).json();
}

// A day's records with their share links, as CSV.
export async function downloadDeliveries(
  planDate: string,
  depotZip: string,
): Promise<{ blob: Blob; filename: string }> {
  const q = new URLSearchParams({ plan_date: planDate, depot_zip: depotZip });
  const resp = await deliveryRequest(`/deliveries/export?${q}`);
  const m = /filename="?([^"]+)"?/.exec(resp.headers.get("Content-Disposition") || "");
  return { blob: await resp.blob(), filename: m?.[1] ?? `deliveries-${planDate}.csv` };
}

// What a share link shows. No login: the token is the only key (a customer
// has no session, so no credentials go with it).
export async function getPublicOrder(token: string): Promise<PublicOrder> {
  return (await deliveryRequest(`/public/orders/${encodeURIComponent(token)}`)).json();
}

// A share link's stamped photo, for an <img>: public, so a plain URL.
export function publicPhotoUrl(token: string, mediaId: string): string {
  return `${API_BASE}/public/orders/${encodeURIComponent(token)}/photos/${encodeURIComponent(mediaId)}`;
}

// Check a file before committing to a solve. Cheap and side-effect free —
// no job is created, so it's safe to call on every file selection.
export async function preflight(
  file: File,
  fleet: FleetOptions,
): Promise<PreflightIssue[]> {
  const body = new FormData();
  body.append("file", file);
  // The earliest departure decides which deadlines can't be met.
  const params = departureParams(
    new URLSearchParams({
      num_26ft: String(fleet.num_26ft),
      num_53ft: String(fleet.num_53ft),
    }),
    fleet,
  );
  const resp = await fetch(`${API_BASE}/preflight?${params}`, {
    method: "POST",
    body,
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`POST /preflight → ${resp.status}`);
  return (await resp.json()).issues;
}

export async function getJob(jobId: string): Promise<JobStatus> {
  const resp = await fetch(`${API_BASE}/jobs/${jobId}`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`GET /jobs/${jobId} → ${resp.status}`);
  return resp.json();
}

// Poll until the job reaches a terminal state. Production will switch this
// for a Supabase Realtime subscription on the job row; the polling path
// stays as a fallback. ``onProgress`` fires on each status read.
export async function pollJob(
  jobId: string,
  onProgress: (status: JobStatus) => void,
  intervalMs = 2000,
): Promise<OptimizeResponseDTO> {
  for (;;) {
    const status = await getJob(jobId);
    onProgress(status);
    if (status.status === "complete") {
      if (!status.result) throw new Error("Job completed without a result.");
      return status.result;
    }
    if (status.status === "error") {
      throw new Error(status.error || "The planner reported an error.");
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}
